package io.jsonize.web.view

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.clickable
import androidx.compose.foundation.ScrollbarStyle
import androidx.compose.foundation.VerticalScrollbar
import androidx.compose.foundation.background
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.rememberScrollbarAdapter
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.DataArray
import androidx.compose.material.icons.filled.DataObject
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowRight
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

@Composable
fun JsonTreeView(root: JsonElement?) {
    if (root == null) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Text("No valid JSON to show", style = MaterialTheme.typography.bodyMedium)
        }
        return
    }

    Surface(
        modifier = Modifier.fillMaxSize(),
        tonalElevation = 1.dp
    ) {
        val scrollState = rememberScrollState()
        Box(Modifier.fillMaxSize()) {
            Column(
                Modifier
                    .fillMaxSize()
                    .verticalScroll(scrollState)
                    .padding(end = 8.dp)
                    .padding(8.dp)
            ) {
                JsonNodeView(name = null, index = null, element = root, depth = 0)
            }

            Box(
                modifier = Modifier
                    .align(Alignment.CenterEnd)
                    .fillMaxHeight()
                    .padding(end = 2.dp)
            ) {
                VerticalScrollbar(
                    adapter = rememberScrollbarAdapter(scrollState),
                    style = ScrollbarStyle(
                        minimalHeight = 24.dp,
                        thickness = 6.dp,
                        shape = MaterialTheme.shapes.small,
                        hoverDurationMillis = 300,
                        unhoverColor = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.3f),
                        hoverColor = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                    )
                )
            }
        }
    }
}

@Composable
private fun JsonNodeView(
    name: String?,
    index: Int?,
    element: JsonElement,
    depth: Int
) {
    val scope = rememberCoroutineScope()
    val indent = Modifier.padding(start = (depth * 12).dp, top = 2.dp, bottom = 2.dp)
    val isContainer = element is JsonObject || element is JsonArray
    var expanded by remember(element, depth) { mutableStateOf(depth <= 1) }
    val hintColor = MaterialTheme.colorScheme.onSurfaceVariant
    val keyColor = MaterialTheme.colorScheme.primary
    val valueColor = MaterialTheme.colorScheme.onSurface
    val interactionSource = remember { MutableInteractionSource() }
    val customRippleColor = remember {
        derivedStateOf {
            if (expanded) Color.Red else Color.Green
        }
    }
    val backgroundAlpha = remember { Animatable(0.2f) }
    var firstRender by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        if (firstRender) {
            backgroundAlpha.animateTo(
                targetValue = 0f,
                animationSpec = tween(
                    durationMillis = 500,
                    easing = LinearOutSlowInEasing
                )
            )
            firstRender = false
        }
    }

    Row(
        modifier = indent
            .fillMaxWidth()
            .background(Color.Green.copy(alpha = backgroundAlpha.value))
            .clickable(enabled = isContainer,
                interactionSource = interactionSource,
                indication = ripple(color = customRippleColor.value)) { expanded = !expanded },
        verticalAlignment = Alignment.CenterVertically
    ) {
        ExpandToggle(
            isContainer = isContainer,
            expanded = expanded,
            tint = hintColor
        )

        if (index != null) {
            Text(
                text = "[$index]",
                color = hintColor,
                style = MaterialTheme.typography.bodySmall
            )
            Spacer(Modifier.width(6.dp))
        }

        if (name != null) {
            SelectionContainer {
                Text(text = name, color = keyColor, style = MaterialTheme.typography.bodySmall)
            }
            Text(text = ": ", color = hintColor, style = MaterialTheme.typography.bodySmall)
        }

        SelectionContainer {
            ValuePreview(
                element = element,
                valueColor = valueColor,
                hintColor = hintColor
            )
        }
    }

    if (expanded) {
        when (element) {
            is JsonObject -> {
                element.entries.forEach { (k, v) ->
                    JsonNodeView(name = k, index = null, element = v, depth = depth + 1)
                }
            }
            is JsonArray -> {
                element.forEachIndexed { idx, v ->
                    JsonNodeView(name = null, index = idx, element = v, depth = depth + 1)
                }
            }
            else -> Unit
        }
    }
}

@Composable
private fun ExpandToggle(
    isContainer: Boolean,
    expanded: Boolean,
    tint: Color
) {
    val iconSize = 18.dp
    if (isContainer) {
        Icon(
            imageVector = if (expanded) Icons.Default.KeyboardArrowDown else Icons.Default.KeyboardArrowRight,
            contentDescription = if (expanded) "Collapse" else "Expand",
            tint = tint,
            modifier = Modifier.size(iconSize).padding(end = 2.dp)
        )
    } else {
        Spacer(Modifier.width(iconSize + 2.dp))
    }
}

@Composable
private fun ValuePreview(
    element: JsonElement,
    valueColor: Color,
    hintColor: Color
) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        when (element) {
            is JsonObject -> {
                Icon(
                    imageVector = Icons.Default.DataObject,
                    contentDescription = "Object",
                    tint = valueColor,
                    modifier = Modifier.size(16.dp)
                )
                Spacer(Modifier.width(6.dp))
                Text("(${element.size} keys)", color = hintColor, style = MaterialTheme.typography.bodySmall)
            }
            is JsonArray -> {
                Icon(
                    imageVector = Icons.Default.DataArray,
                    contentDescription = "Array",
                    tint = valueColor,
                    modifier = Modifier.size(16.dp)
                )
                Spacer(Modifier.width(6.dp))
                Text("(${element.size} items)", color = hintColor, style = MaterialTheme.typography.bodySmall)
            }
            is JsonPrimitive -> {
                Text(
                    primitiveText(element),
                    color = valueColor,
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }
    }
}

private fun primitiveText(element: JsonPrimitive): String {
    return if (element.isString) element.content else element.toString()
}
